/*
 * The Google profile editor (2026-11-01, owner: "Move to sign in so it works one way, and also make a
 * multi editor for brand accounts only, & a mini version for companies/franchise location with sub locations").
 *
 * A brand gets every profile of the brand and its locations in one table, ticks several and changes hours, holiday hours
 * or the website on all of them. A location gets the mini version: its own profile and its sublocations', one at a time,
 * with no name or phone (the brand sets those). Products can't be changed by an app on Google, so they are not here.
 */
import { useState } from "react";
import {
  hoursProblems,
  hoursWords,
  specialProblems,
  WEEK_DAY_WORDS,
  WEEK_DAYS,
  type GbpBulkAnswer,
  type GbpPatch,
  type GbpProfileRow,
  type GbpProfilesAnswer,
  type GbpService,
  type HoursRow,
  type SpecialDay,
  type WeekDay,
} from "@steward/shared";
import { useGbpAttach, useGbpBulk, useGbpDetach, useGbpPatch, useGbpProfiles, useGoogleLocations } from "../api/marketing";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, InputText } from "../components/fields";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, EmptyState, LoadError, Skeleton, StatusLabel } from "../components/parts";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

// The profiles -----------------------------------------------------------------------------------------------------------

function statusOf(p: GbpProfileRow) {
  if (p.error) return <StatusLabel status="critical">Can't reach</StatusLabel>;
  if (p.pending) return <StatusLabel status="warning">Google is checking a change</StatusLabel>;
  if (p.googleUpdated) return <StatusLabel status="informative">Google changed something</StatusLabel>;
  return <StatusLabel status="success">Live</StatusLabel>;
}
const whereOf = (p: GbpProfileRow) => (p.subLocation ? `${p.companyName}, ${p.subLocation}` : p.companyName);

export function ProfileEditor() {
  const { company, can } = useSession();
  const q = useGbpProfiles(company.id);
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<"special" | "hours" | "website" | null>(null);
  const [adding, setAdding] = useState(false);
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  // Nothing while it loads: before sign in the list stays hidden, so a placeholder would only flash and go.
  if (!q.data) return null;
  const a = q.data;
  if (a.mode === "hq")
    return <Banner>Open a brand or a location to change its Google profiles. Head office has none of its own.</Banner>;
  const current = a.profiles.find((p) => p.id === open) ?? null;
  const addBtn =
    edit && a.link.signedIn ? (
      <Button type="secondary" size="small" icon="plus" onClick={() => setAdding(true)}>
        Add a profile
      </Button>
    ) : null;
  // 2026-11-01 owner: no list of profiles that can't be reached before sign in. The listing card above has the button.
  if (!a.link.signedIn)
    return a.link.signedOut ? (
      <Banner type="warning">
        Google signed The Steward out. Sign in with Google again above, or reviews stop being read and your profiles can't be
        changed.
      </Banner>
    ) : null;
  const ids = [...picked].filter((id) => a.profiles.some((p) => p.id === id && p.reachable));
  return (
    <>
      {a.mode === "brand" ? (
        <DataTable
          title="Google profiles"
          rows={a.profiles}
          rowKey={(p) => p.id}
          onRowClick={(p) => setOpen(p.id)}
          tools={addBtn}
          {...(edit ? { selected: picked, onSelect: setPicked } : {})}
          bulk={
            <>
              <Button size="small" type="secondary" onClick={() => setBulk("special")}>
                Holiday hours
              </Button>
              <Button size="small" type="secondary" onClick={() => setBulk("hours")}>
                Hours
              </Button>
              <Button size="small" type="secondary" onClick={() => setBulk("website")}>
                Website
              </Button>
            </>
          }
          empty={<p className="muted pad">No profiles yet. Sign in with Google, then add your profiles.</p>}
          columns={[
            {
              key: "title",
              header: "Profile",
              sort: (p) => p.title,
              render: (p) => (
                <div className="stack tight">
                  <b>{p.title}</b>
                  <span className="small muted">{p.address}</span>
                </div>
              ),
            },
            { key: "where", header: "Location", sort: whereOf, render: whereOf },
            { key: "hours", header: "Hours", render: (p) => (p.reachable ? hoursWords(p.hours) : "") },
            { key: "web", header: "Website", render: (p) => p.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") },
            { key: "status", header: "Status", render: statusOf },
          ]}
        />
      ) : (
        <Card title="Google profiles" action={addBtn} flush>
          {a.profiles.length === 0 ? (
            <p className="muted pad">No profiles yet. Sign in with Google, then add your profile and each sublocation's.</p>
          ) : (
            <div className="list">
              {a.profiles.map((p) => (
                <button key={p.id} type="button" className="row" onClick={() => setOpen(p.id)}>
                  <div className="tx">
                    <b>{p.subLocation ? `${p.title}, ${p.subLocation}` : p.title}</b>
                    <span>{p.reachable ? hoursWords(p.hours) : (p.error ?? "")}</span>
                  </div>
                  {statusOf(p)}
                </button>
              ))}
            </div>
          )}
          {edit && a.profiles.filter((p) => p.reachable).length > 1 ? (
            <div className="row end pad">
              <Button
                type="secondary"
                size="small"
                onClick={() => {
                  setPicked(new Set(a.profiles.filter((p) => p.reachable).map((p) => p.id)));
                  setBulk("special");
                }}
              >
                Holiday hours for all
              </Button>
            </div>
          ) : null}
        </Card>
      )}
      {current ? <EditProfile key={current.id} p={current} mode={a.mode} canEdit={edit} onClose={() => setOpen(null)} /> : null}
      {bulk && ids.length ? (
        <BulkEdit
          what={bulk}
          profiles={a.profiles.filter((p) => ids.includes(p.id))}
          onClose={() => {
            setBulk(null);
            setPicked(new Set());
          }}
        />
      ) : null}
      {adding ? <AddProfile a={a} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

// Hours, holiday hours and services -------------------------------------------------------------------------------------

type DayKind = "closed" | "open" | "24";

function HoursEditor({ value, onChange }: { value: HoursRow[]; onChange: (v: HoursRow[]) => void }) {
  const kindOf = (d: WeekDay): DayKind => {
    const rows = value.filter((r) => r.day === d);
    if (!rows.length) return "closed";
    return rows.length === 1 && rows[0]!.open === "00:00" && rows[0]!.close === "24:00" ? "24" : "open";
  };
  const setDay = (d: WeekDay, rows: HoursRow[]) => onChange([...value.filter((r) => r.day !== d), ...rows]);
  return (
    <div className="stack">
      {WEEK_DAYS.map((d) => {
        const rows = value.filter((r) => r.day === d);
        const kind = kindOf(d);
        return (
          <div key={d} className="stack tight">
            <Choice<DayKind>
              label={WEEK_DAY_WORDS[d]}
              value={kind}
              onChange={(k) =>
                setDay(
                  d,
                  k === "closed"
                    ? []
                    : k === "24"
                      ? [{ day: d, open: "00:00", close: "24:00" }]
                      : [{ day: d, open: "08:00", close: "17:00" }],
                )
              }
              options={[
                { value: "open", label: "Open" },
                { value: "closed", label: "Closed" },
                { value: "24", label: "24 hours" },
              ]}
            />
            {kind === "open"
              ? rows.map((r, i) => (
                  <div key={i} className="row tight">
                    <InputText
                      label="Opens"
                      type="time"
                      value={r.open}
                      onChange={(e) =>
                        setDay(
                          d,
                          rows.map((x, j) => (j === i ? { ...x, open: e.target.value } : x)),
                        )
                      }
                    />
                    <InputText
                      label="Closes"
                      type="time"
                      value={r.close === "24:00" ? "23:59" : r.close}
                      onChange={(e) =>
                        setDay(
                          d,
                          rows.map((x, j) => (j === i ? { ...x, close: e.target.value } : x)),
                        )
                      }
                    />
                    {rows.length > 1 ? (
                      <Button
                        type="tertiary"
                        size="small"
                        onClick={() =>
                          setDay(
                            d,
                            rows.filter((_x, j) => j !== i),
                          )
                        }
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                ))
              : null}
            {kind === "open" && rows.length === 1 ? (
              <div>
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => setDay(d, [...rows, { day: d, open: "13:00", close: "17:00" }])}
                >
                  Add a second time (a lunch break)
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SpecialEditor({ value, onChange }: { value: SpecialDay[]; onChange: (v: SpecialDay[]) => void }) {
  const set = (i: number, d: Partial<SpecialDay>) => onChange(value.map((x, j) => (j === i ? { ...x, ...d } : x)));
  return (
    <div className="stack">
      {value.length === 0 ? <p className="muted">No holiday hours.</p> : null}
      {value.map((d, i) => (
        <div key={i} className="stack tight">
          <div className="row tight">
            <InputText label="Day" type="date" value={d.date} onChange={(e) => set(i, { date: e.target.value })} />
            <Choice<"closed" | "open">
              label="That day"
              value={d.closed ? "closed" : "open"}
              onChange={(k) =>
                set(
                  i,
                  k === "closed" ? { closed: true, open: null, close: null } : { closed: false, open: "09:00", close: "13:00" },
                )
              }
              options={[
                { value: "closed", label: "Closed" },
                { value: "open", label: "Other hours" },
              ]}
            />
            <Button type="tertiary" size="small" onClick={() => onChange(value.filter((_x, j) => j !== i))}>
              Remove
            </Button>
          </div>
          {!d.closed ? (
            <div className="row tight">
              <InputText label="Opens" type="time" value={d.open ?? ""} onChange={(e) => set(i, { open: e.target.value })} />
              <InputText label="Closes" type="time" value={d.close ?? ""} onChange={(e) => set(i, { close: e.target.value })} />
            </div>
          ) : null}
        </div>
      ))}
      <div>
        <Button
          type="tertiary"
          size="small"
          icon="plus"
          onClick={() =>
            onChange([...value, { date: new Date().toISOString().slice(0, 10), closed: true, open: null, close: null }])
          }
        >
          Add a day
        </Button>
      </div>
    </div>
  );
}

function ServicesEditor({ value, onChange }: { value: GbpService[]; onChange: (v: GbpService[]) => void }) {
  const set = (i: number, s: Partial<GbpService>) => onChange(value.map((x, j) => (j === i ? { ...x, ...s } : x)));
  return (
    <div className="stack">
      {value.map((s, i) => (
        <div key={`${s.key}-${String(i)}`} className="row tight">
          <InputText label="Service" value={s.name} disabled={!s.custom} onChange={(e) => set(i, { name: e.target.value })} />
          <InputText
            label="What it is"
            wide
            maxLength={300}
            value={s.description}
            onChange={(e) => set(i, { description: e.target.value })}
          />
          <Button type="tertiary" size="small" onClick={() => onChange(value.filter((_x, j) => j !== i))}>
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="tertiary"
          size="small"
          icon="plus"
          onClick={() => onChange([...value, { key: "new", name: "", description: "", custom: true }])}
        >
          Add a service
        </Button>
      </div>
    </div>
  );
}

// One profile ------------------------------------------------------------------------------------------------------------

function EditProfile({
  p,
  mode,
  canEdit,
  onClose,
}: {
  p: GbpProfileRow;
  mode: GbpProfilesAnswer["mode"];
  canEdit: boolean;
  onClose: () => void;
}) {
  const save = useGbpPatch();
  const detach = useGbpDetach();
  const toast = useToast();
  const [title, setTitle] = useState(p.title);
  const [phone, setPhone] = useState(p.phone);
  const [website, setWebsite] = useState(p.website);
  const [description, setDescription] = useState(p.description);
  const [hours, setHours] = useState(p.hours);
  const [special, setSpecial] = useState(p.specialHours);
  const [services, setServices] = useState(p.services);
  const brand = mode === "brand";
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const patch: GbpPatch = {
    ...(brand && title.trim() !== p.title ? { title: title.trim() } : {}),
    ...(brand && phone.trim() !== p.phone ? { phone: phone.trim() } : {}),
    ...(website.trim() !== p.website ? { website: website.trim() } : {}),
    ...(description.trim() !== p.description ? { description: description.trim() } : {}),
    ...(!same(hours, p.hours) ? { hours } : {}),
    ...(!same(special, p.specialHours) ? { specialHours: special } : {}),
    ...(!same(services, p.services) ? { services: services.filter((s) => s.name.trim()) } : {}),
  };
  const problems = [...hoursProblems(hours), ...specialProblems(special)];
  const changed = Object.keys(patch).length > 0;
  return (
    <Modal
      title={p.subLocation ? `${p.title}, ${p.subLocation}` : p.title}
      open
      onClose={onClose}
      size="full"
      footer={
        canEdit && p.reachable ? (
          <Button
            loading={save.isPending}
            disabled={!changed || problems.length > 0}
            onClick={() =>
              save.mutate(
                { id: p.id, patch },
                {
                  onSuccess: () => {
                    toast("Saved on Google");
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Save on Google
          </Button>
        ) : null
      }
    >
      <div className="stack">
        {p.error ? <Banner type="warning">{p.error}</Banner> : null}
        {p.pending ? <Banner>Google is still checking a change on this profile. It shows once Google is done.</Banner> : null}
        {p.googleUpdated ? (
          <Banner>Google changed something on this profile from a suggestion. What shows here is what Google has now.</Banner>
        ) : null}
        <p className="small muted">
          {[p.address, p.category, p.reviewCount ? `${p.rating?.toFixed(1) ?? ""} stars, ${String(p.reviewCount)} reviews` : null]
            .filter(Boolean)
            .join(" · ")}
          {p.mapsUrl ? (
            <>
              {" · "}
              <a href={p.mapsUrl} target="_blank" rel="noreferrer">
                Open on Google
              </a>
            </>
          ) : null}
        </p>
        {p.reachable ? (
          <>
            <Card title="Name and contact">
              <div className="stack">
                {brand ? null : <p className="muted">The brand sets the name and phone. Ask the brand owner to change them.</p>}
                <InputText
                  label="Name on Google"
                  value={title}
                  disabled={!brand || !canEdit}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {brand && title.trim() !== p.title ? (
                  <Banner type="warning">
                    A new name can make Google check the profile again, or suspend it. Use the real business name with no extra
                    words.
                  </Banner>
                ) : null}
                <InputText
                  label="Phone"
                  type="tel"
                  value={phone}
                  disabled={!brand || !canEdit}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <InputText
                  label="Website"
                  type="url"
                  placeholder="https://"
                  value={website}
                  disabled={!canEdit}
                  onChange={(e) => setWebsite(e.target.value)}
                />
                <label className="field wide">
                  <span className="label">{`Description, ${String(description.length)} of 750`}</span>
                  <textarea
                    className="textarea"
                    rows={5}
                    maxLength={750}
                    value={description}
                    disabled={!canEdit}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </label>
              </div>
            </Card>
            <Card title="Hours">
              <HoursEditor value={hours} onChange={setHours} />
            </Card>
            <Card title="Holiday hours">
              <SpecialEditor value={special} onChange={setSpecial} />
            </Card>
            <Card title="Services">
              <p className="muted">Google's own services keep their name. Prices stay as they are on Google.</p>
              <ServicesEditor value={services} onChange={setServices} />
            </Card>
            <p className="small muted">Products can only be changed on Google itself.</p>
          </>
        ) : null}
        {problems.length ? <Banner type="warning">{problems.join(" ")}</Banner> : null}
        {canEdit ? (
          <div>
            <Button
              type="tertiary"
              size="small"
              loading={detach.isPending}
              onClick={() =>
                detach.mutate(p.id, {
                  onSuccess: () => {
                    toast("Taken off. Google keeps the profile as it is.");
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                })
              }
            >
              Take off this list
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

// Several at once --------------------------------------------------------------------------------------------------------

function BulkEdit({
  what,
  profiles,
  onClose,
}: {
  what: "special" | "hours" | "website";
  profiles: GbpProfileRow[];
  onClose: () => void;
}) {
  const bulk = useGbpBulk();
  const toast = useToast();
  const [hours, setHours] = useState<HoursRow[]>(profiles[0]?.hours ?? []);
  const [special, setSpecial] = useState<SpecialDay[]>([]);
  const [website, setWebsite] = useState(profiles[0]?.website ?? "");
  const [done, setDone] = useState<GbpBulkAnswer | null>(null);
  const problems = what === "hours" ? hoursProblems(hours) : what === "special" ? specialProblems(special) : [];
  const title = what === "special" ? "Holiday hours" : what === "hours" ? "Hours" : "Website";
  const ready = what === "special" ? special.length > 0 : what === "website" ? /^https:\/\//i.test(website.trim()) : true;
  return (
    <Modal
      title={`${title} for ${String(profiles.length)} ${profiles.length === 1 ? "profile" : "profiles"}`}
      open
      onClose={onClose}
      size="full"
      footer={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <Button
            loading={bulk.isPending}
            disabled={!ready || problems.length > 0}
            onClick={() =>
              bulk.mutate(
                {
                  ids: profiles.map((p) => p.id),
                  set:
                    what === "special" ? { specialHours: special } : what === "hours" ? { hours } : { website: website.trim() },
                },
                { onSuccess: setDone, onError: (e) => toast(errText(e), { kind: "error" }) },
              )
            }
          >
            Save on Google
          </Button>
        )
      }
    >
      <div className="stack">
        {done ? (
          <div className="list">
            {done.results.map((r) => (
              <div key={r.id} className="row">
                <div className="tx">
                  <b>{r.title}</b>
                  {r.error ? <span>{r.error}</span> : null}
                </div>
                <StatusLabel status={r.ok ? "success" : "critical"}>{r.ok ? "Saved" : "Not saved"}</StatusLabel>
              </div>
            ))}
          </div>
        ) : (
          <>
            <p className="muted">{profiles.map((p) => whereOf(p)).join(", ")}</p>
            {what === "special" ? (
              <>
                <p className="muted">These days are added to each profile. A day already there is replaced.</p>
                <SpecialEditor value={special} onChange={setSpecial} />
              </>
            ) : what === "hours" ? (
              <>
                <p className="muted">These hours replace the hours on every profile you ticked.</p>
                <HoursEditor value={hours} onChange={setHours} />
              </>
            ) : (
              <InputText
                label="Website"
                type="url"
                placeholder="https://"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            )}
            {problems.length ? <Banner type="warning">{problems.join(" ")}</Banner> : null}
          </>
        )}
      </div>
    </Modal>
  );
}

// Adding a profile -------------------------------------------------------------------------------------------------------

function AddProfile({ a, onClose }: { a: GbpProfilesAnswer; onClose: () => void }) {
  const { company } = useSession();
  const q = useGoogleLocations(company.id, true);
  const attach = useGbpAttach();
  const toast = useToast();
  const [name, setName] = useState<string | null>(null);
  const [where, setWhere] = useState(a.places[0]?.companyId ?? company.id);
  const [sub, setSub] = useState("");
  const place = a.places.find((x) => x.companyId === where);
  const free = (q.data ?? []).filter((l) => !l.attached);
  return (
    <Modal
      title="Add a profile"
      open
      onClose={onClose}
      footer={
        <Button
          loading={attach.isPending}
          disabled={!name}
          onClick={() =>
            name &&
            attach.mutate(
              { name, companyId: where, subLocation: sub || null },
              {
                onSuccess: () => {
                  toast("Profile added");
                  onClose();
                },
                onError: (e) => toast(errText(e), { kind: "error" }),
              },
            )
          }
        >
          Add
        </Button>
      }
    >
      <div className="stack">
        {q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : null}
        {!q.data && !q.error ? <Skeleton rows={3} /> : null}
        {q.data && free.length === 0 ? (
          <EmptyState icon="marketing" title="Nothing new to add">
            Every profile this Google account manages is on the list already.
          </EmptyState>
        ) : null}
        {free.length ? (
          <div className="list">
            {free.map((l) => (
              <label key={l.name} className="row">
                <input type="radio" name="gbp-add" checked={name === l.name} onChange={() => setName(l.name)} />
                <div className="tx">
                  <b>{l.title}</b>
                  <span>{l.address || (l.placeId ? "" : "Not on Maps yet")}</span>
                </div>
              </label>
            ))}
          </div>
        ) : null}
        {a.places.length > 1 ? (
          <label className="field">
            <span className="label">Which location</span>
            <select
              className="select"
              value={where}
              onChange={(e) => {
                setWhere(e.target.value);
                setSub("");
              }}
            >
              {a.places.map((x) => (
                <option key={x.companyId} value={x.companyId}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {place?.subLocations.length ? (
          <label className="field">
            <span className="label">Sublocation</span>
            <select className="select" value={sub} onChange={(e) => setSub(e.target.value)}>
              <option value="">{`${place.name} itself`}</option>
              {place.subLocations.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
